// The far canopy as nested rings of rasters, and the one way the ground shader reads it. Wire: canopyWire.ts; server: simulation/src/ecocanopy.py, ecocanopywire.py; design: claude/ecology-lod-design.md.
//
// The server sends each viewer a canopy window for ground rings 8 to 15 (RING_N x RING_N vertices, dx = 0.04 m * 2^k = 10 m to 1.3 km), on the same lattice and under the same hysteresis as the ground's own
// windows of those rings (groundClip.ts). A vertex says what grows over its box: the leaf area index (m2 of leaf per m2 of ground), the canopy height, the crown width, and how much of the leaf area is tree,
// shrub, fern and grass.
//
// The store keeps the newest window of each ring as two array textures, one layer per ring, and holds them as the sums the server made them from, not as the ratios it sends: texture A is
// (L, L h, L_woody w, L tree), texture B is (L shrub, L fern, L grass, L tone), with L the leaf area index and L_woody the tree and shrub part of it. The sums add, so the sampler's bilinear filter (and the blend
// between rings) averages them correctly; the shader divides by L (by L_woody for the crown width) after the lookup to get a height, a crown width, a tone and shares. Half floats are enough (11 bits; L h reaches
// about 1300).
//
// The windows also carry the artist's look (the leaf palette, the cover law): the store keeps the newest and puts it in the uniforms `uCPal`, `uCK` and `uCGap`.
//
// What the ground shader does with it is canopyLook.ts. The lookup here is `canopyAt(rel, dist, A, B)`: the finest ring that holds the point, no finer than the eye's distance warrants (one vertex about as
// wide as a pixel is long, times CANOPY_LOD_ANGLE), blended into the next coarser ring near its window's edge and where the distance is between two rings.
import * as THREE from 'three';
import { RING_N, DX0 } from './groundWire';
import { CANOPY_PLANES, CANOPY_RING_FIRST, CANOPY_RING_LAST, decodeLog, type CanopyLook, type CanopyWindow } from './canopyWire';

/** Number of rings that have a canopy window (layers of the array textures). */
export const CANOPY_LAYERS = CANOPY_RING_LAST - CANOPY_RING_FIRST + 1;
/** The angle (rad) one canopy vertex should span from the eye: the ring is chosen so that vertices are about this far apart as seen. A pixel of a 60 degree, 1080 line picture is about 0.001 rad. */
export const CANOPY_LOD_ANGLE = 0.004;
/** Distances (m) over which the canopy fades in on the ground: nothing nearer than the first, all of it beyond the second. The smallest plants (ecoWire.ts, grass to 20 m, shrubs to 100 m) fade out
 *  over the last quarter of their radius while it comes in, so the ground is never bare between them; the tall plants stand out of it to 300 m and 1 km. */
export const CANOPY_FADE_DEFAULT: [number, number] = [12, 90];
/** Cells in from a window's edge over which the blend into the next coarser ring goes from all of it (at the edge) to none. */
const EDGE_CELLS = 8;
/** Cells at a window's edge that are never read (the filter reads one cell past a vertex). */
const GUARD_CELLS = 2;

const nn = RING_N * RING_N;
const NCH = 4;

/** The lookup in GLSL. Needs the uniforms of `CanopyClip.uniforms`. `rel` is a position counted from the floating origin (x east, z south = three's axes). */
export const CANOPY_GLSL = /* glsl */`
uniform highp sampler2DArray uCanA;     // (L, L h, L_woody w, L tree) per ring: layer k - ${CANOPY_RING_FIRST} (half floats)
uniform highp sampler2DArray uCanB;     // (L shrub, L fern, L grass, L tone)
uniform vec4  uCWin[${CANOPY_LAYERS}];            // per ring: x, z offset of vertex (0,0) from O (z already negated), 1 / dx, n (0: no window)
uniform float uCOn;                     // 1 when the canopy is drawn
uniform vec2  uCFade;                   // distance (m) where it starts to show and where it is all there
uniform float uCAngle;                  // the angle one vertex should span (rad)
uniform vec3  uCPal[16];                // the artist's palette: per form (tree, shrub, fern, grass) dry shade, dry lit, lush shade, lush lit (linear rgb)
uniform float uCK;                      // cover = 1 - exp(-uCK L)
uniform float uCGap;                    // the shade between and under crowns (1: none)

// the first ring from layer i0 up whose window holds t with a guard; edge is 0 at the guard and 1 ${EDGE_CELLS} cells in
int canopyPick(vec2 rel, int i0, out vec2 uv, out float edge) {
  for (int i = 0; i < ${CANOPY_LAYERS}; i++) {
    if (i < i0) continue;
    vec4 w = uCWin[i];
    if (w.w < 0.5) continue;
    vec2 t = vec2((rel.x - w.x) * w.z, (w.y - rel.y) * w.z);
    float d = min(min(t.x, t.y), min(w.w - 1.0 - t.x, w.w - 1.0 - t.y)) - ${GUARD_CELLS.toFixed(1)};
    if (d >= 0.0) { uv = (t + 0.5) / w.w; edge = clamp(d / ${EDGE_CELLS.toFixed(1)}, 0.0, 1.0); return i; }
  }
  edge = 0.0; uv = vec2(0.0);
  return -1;
}
// the canopy at rel seen from dist metres away; A and B are the textures' values (all zero where no window holds the point)
void canopyAt(vec2 rel, float dist, out vec4 A, out vec4 B) {
  A = vec4(0.0); B = vec4(0.0);
  float lod = clamp(log2(max(dist * uCAngle, 1.0e-3) / ${(DX0 * 2 ** CANOPY_RING_FIRST).toFixed(2)}), 0.0, ${(CANOPY_LAYERS - 1).toFixed(1)});
  int i0 = int(floor(lod));
  float f = fract(lod);
  vec2 uv; float edge;
  int a = canopyPick(rel, i0, uv, edge);
  if (a < 0) return;
  A = textureLod(uCanA, vec3(uv, float(a)), 0.0);
  B = textureLod(uCanB, vec3(uv, float(a)), 0.0);
  float toNext = 1.0 - edge;
  if (a == i0) toNext = max(toNext, f);
  if (toNext > 0.001 && a + 1 < ${CANOPY_LAYERS}) {
    vec2 uv2; float edge2;
    int b = canopyPick(rel, a + 1, uv2, edge2);
    if (b >= 0) {
      A = mix(A, textureLod(uCanA, vec3(uv2, float(b)), 0.0), toNext);
      B = mix(B, textureLod(uCanB, vec3(uv2, float(b)), 0.0), toNext);
    }
  }
}
`;

export type CanopyUniforms = {
  uCanA: { value: THREE.DataArrayTexture };
  uCanB: { value: THREE.DataArrayTexture };
  uCWin: { value: THREE.Vector4[] };
  uCOn: { value: number };
  uCFade: { value: THREE.Vector2 };
  uCAngle: { value: number };
  uCPal: { value: THREE.Vector3[] };
  uCK: { value: number };
  uCGap: { value: number };
};

function arrayTexture(data: Uint16Array): THREE.DataArrayTexture {
  const t = new THREE.DataArrayTexture(data as unknown as ConstructorParameters<typeof THREE.DataArrayTexture>[0], RING_N, RING_N, CANOPY_LAYERS);
  t.format = THREE.RGBAFormat; t.type = THREE.HalfFloatType;
  t.minFilter = t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.unpackAlignment = 1;
  t.needsUpdate = true;
  return t;
}

/** A log code table: the value of each of the 256 codes of a plane with range `r`. */
function table(r: [number, number]): Float32Array {
  const t = new Float32Array(256);
  for (let c = 0; c < 256; c++) t[c] = decodeLog(c, r);
  return t;
}

export class CanopyClip {
  /** the textures' data, (layer, row, column, channel): public for the tests */
  readonly aData = new Uint16Array(nn * NCH * CANOPY_LAYERS);
  readonly bData = new Uint16Array(nn * NCH * CANOPY_LAYERS);
  readonly uniforms: CanopyUniforms;
  private win: (CanopyWindow | null)[] = new Array(CANOPY_LAYERS).fill(null);
  private dirty = false;
  private lastOx = NaN; private lastOz = NaN;
  /** windows taken since construction (a counter for the overlay and the tests) */
  taken = 0;

  constructor() {
    const wins: THREE.Vector4[] = [];
    for (let i = 0; i < CANOPY_LAYERS; i++) wins.push(new THREE.Vector4(0, 0, 1, 0));
    this.uniforms = {
      uCanA: { value: arrayTexture(this.aData) },
      uCanB: { value: arrayTexture(this.bData) },
      uCWin: { value: wins },
      uCOn: { value: 1 },
      uCFade: { value: new THREE.Vector2(CANOPY_FADE_DEFAULT[0], CANOPY_FADE_DEFAULT[1]) },
      uCAngle: { value: CANOPY_LOD_ANGLE },
      uCPal: { value: Array.from({ length: 16 }, () => new THREE.Vector3(0.2, 0.2, 0.2)) },
      uCK: { value: 0.7 },
      uCGap: { value: 0.62 },
    };
  }

  /** Draw the canopy (default) or leave it out. */
  setEnabled(on: boolean): void { this.uniforms.uCOn.value = on ? 1 : 0; }
  get enabled(): boolean { return this.uniforms.uCOn.value > 0.5; }
  /** Distances (m) over which the canopy fades in. */
  setFade(from: number, full: number): void {
    if (!(from >= 0 && full > from)) throw new Error(`canopy fade ${from}, ${full}: want 0 <= from < full`);
    this.uniforms.uCFade.value.set(from, full);
  }
  /** The rings that have a window (ring numbers, finest first). */
  have(): number[] { const r: number[] = []; for (let i = 0; i < CANOPY_LAYERS; i++) if (this.win[i]) r.push(CANOPY_RING_FIRST + i); return r; }
  windowOf(ring: number): CanopyWindow | null { return this.win[ring - CANOPY_RING_FIRST] ?? null; }

  /** Take a window from the wire: a ring's window replaces the ring's last. */
  accept(w: CanopyWindow): void {
    const m = w.meta, k = m.stream, l = k - CANOPY_RING_FIRST;
    if (l < 0 || l >= CANOPY_LAYERS || m.n !== RING_N || Math.abs(m.dx - DX0 * 2 ** k) > 1e-9 || m.nf !== 4) {
      throw new Error(`canopy window of ring ${k}: n ${m.n}, dx ${m.dx}, ${m.nf} forms is not a canopy ring of this client (rings ${CANOPY_RING_FIRST} to ${CANOPY_RING_LAST}, n ${RING_N}, 4 forms)`);
    }
    this.setLook(m.look);
    this.taken++;
    this.win[l] = w;
    const tl = table(m.q.lai), th = table(m.q.h), tw = table(m.q.w);
    const p = w.planes;
    const A = this.aData.subarray(l * nn * NCH, (l + 1) * nn * NCH), B = this.bData.subarray(l * nn * NCH, (l + 1) * nn * NCH);
    const hf = THREE.DataUtils.toHalfFloat;
    const zero = hf(0);
    const P = CANOPY_PLANES;
    for (let i = 0; i < nn; i++) {
      const lai = tl[p[i]!]!;
      const o = i * NCH;
      if (!(lai > 0)) { A[o] = A[o + 1] = A[o + 2] = A[o + 3] = zero; B[o] = B[o + 1] = B[o + 2] = B[o + 3] = zero; continue; }
      const s0 = p[P * nn + i]!, s1 = p[(P + 1) * nn + i]!, s2 = p[(P + 2) * nn + i]!, s3 = p[(P + 3) * nn + i]!;
      const S = s0 + s1 + s2 + s3;
      const k1 = S > 0 ? lai / S : 0;                                    // the shares are bytes that need not add to 255: renormalise
      const woody = k1 * (s0 + s1);
      A[o] = hf(lai); A[o + 1] = hf(lai * th[p[nn + i]!]!); A[o + 2] = hf(woody * tw[p[2 * nn + i]!]!); A[o + 3] = hf(k1 * s0);
      B[o] = hf(k1 * s1); B[o + 1] = hf(k1 * s2); B[o + 2] = hf(k1 * s3); B[o + 3] = hf(lai * (p[3 * nn + i]! / 255));
    }
    this.dirty = true;
  }

  /** Take the artist's look from a window (the newest window's wins). */
  private setLook(look: CanopyLook): void {
    if (!look || !Array.isArray(look.pal) || look.pal.length !== 4 || !look.pal.every(f => Array.isArray(f) && f.length === 4 && f.every(t => Array.isArray(t) && t.length === 3 && t.every(c => Number.isFinite(c))))
        || !(look.k > 0) || !(look.gap > 0 && look.gap <= 1)) {
      throw new Error(`the canopy window's look is not usable: ${JSON.stringify(look)?.slice(0, 160)}`);
    }
    const pal = this.uniforms.uCPal.value;
    for (let f = 0; f < 4; f++) for (let t = 0; t < 4; t++) pal[f * 4 + t]!.set(look.pal[f]![t]![0]!, look.pal[f]![t]![1]!, look.pal[f]![t]![2]!);
    this.uniforms.uCK.value = look.k;
    this.uniforms.uCGap.value = look.gap;
  }

  /** Once per frame, before drawing: upload what came, and put each ring's offset from the floating origin (ox, oz) into the uniforms. Returns true when a window was taken. */
  sync(ox: number, oz: number): boolean {
    const changed = this.dirty;
    if (this.dirty) {
      this.dirty = false;
      this.uniforms.uCanA.value.needsUpdate = true;
      this.uniforms.uCanB.value.needsUpdate = true;
    }
    if (changed || ox !== this.lastOx || oz !== this.lastOz) {
      this.lastOx = ox; this.lastOz = oz;
      const wins = this.uniforms.uCWin.value;
      for (let i = 0; i < CANOPY_LAYERS; i++) {
        const w = this.win[i];
        if (!w) { wins[i]!.set(0, 0, 1, 0); continue; }
        const m = w.meta;
        wins[i]!.set(m.x0 - ox, -m.y0 - oz, 1 / m.dx, RING_N);
      }
    }
    return changed;
  }

  /** Forget everything (a new connection streams the canopy again from nothing). */
  reset(): void {
    this.win.fill(null);
    this.aData.fill(0); this.bData.fill(0);
    this.dirty = true;
    this.lastOx = NaN; this.lastOz = NaN;
  }

  dispose(): void {
    this.uniforms.uCanA.value.dispose(); this.uniforms.uCanB.value.dispose();
  }
}
